import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'
import {
  TextMaskCreditCard,
  TextMaskCreditCardDate,
  TextMaskPhoneNumber,
  TextMaskZipcode,
} from '../../components/maskedInputs'
import { API_PATH_ADMIN_CREATE_CUSTOMER_RENTAL } from '../../config'
import { STATES } from '../../config/states'
import { getApiConfig, getLocalServerURL } from '../../utils/getApiConfig'
import { onError } from '../../utils/onError'
import { onSuccess } from '../../utils/onSuccess'
import { BankAccountTypeEnum } from '../Profile/types'
import { getUnitsIndividual, getUnitsMaster } from '../Units/selectors'
import {
  getRequiredCardNumberError,
  getRequiredEmailError,
  getRequiredExpirationDateError,
  getRequiredPhoneError,
  getRequiredTextError,
  getRequiredZipError,
} from './validation'

type Props = {
  mode?: 'customerRental' | 'profileOnly'
  open: boolean
  onClose: () => void
  onCompleted: (result: {
    customerProfileId: number
    customerName: string
    claimCode: string
  }) => void
}

type CustomerState = {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
}

type PaymentState = {
  address: string
  city: string
  state: string
  zip: string
  cardNumber: string
  expirationDate: string
  routingNumber: string
  accountNumber: string
  nameOnAccount: string
  accountType: BankAccountTypeEnum
}

type ShippingState = {
  firstName: string
  lastName: string
  phoneNumber: string
  address: string
  city: string
  state: string
  zip: string
}

type InventoryGroup = {
  masterId: string
  label: string
  price: number
  availableUnits: Array<{
    id: string
    masterId: string
    unitNumber: string
    size: string
    isClimate: boolean
    isCovered: boolean
  }>
}

const RENTAL_STEPS = [
  'Customer',
  'Billing',
  'Unit mix',
  'Unit locations',
  'Summary',
]

const PROFILE_ONLY_STEPS = ['Customer', 'Billing', 'Summary']

const EMPTY_CUSTOMER: CustomerState = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
}

const EMPTY_PAYMENT: PaymentState = {
  address: '',
  city: '',
  state: 'TX',
  zip: '',
  cardNumber: '',
  expirationDate: '',
  routingNumber: '',
  accountNumber: '',
  nameOnAccount: '',
  accountType: BankAccountTypeEnum.CHECKING,
}

const getInventoryLabel = ({
  isClimate,
  isCovered,
  size,
}: {
  size: string
  isClimate?: boolean
  isCovered?: boolean
}) =>
  [size, isClimate ? 'Climate' : 'Standard', isCovered ? 'Covered' : null]
    .filter(Boolean)
    .join(' · ')

const getSummaryTotal = (
  selectedUnits: InventoryGroup['availableUnits'],
  priceByMasterId: Record<string, number>
) =>
  selectedUnits.reduce(
    (total, unit) => total + Number(priceByMasterId[unit.masterId] ?? 0),
    0
  )

const disabledButtonSx = {
  '&.Mui-disabled': {
    backgroundColor: 'rgba(71, 85, 105, 0.38)',
    borderColor: 'rgba(100, 116, 139, 0.34)',
    boxShadow: 'none',
    color: 'rgba(226, 232, 240, 0.52)',
  },
  '&.MuiButton-contained.Mui-disabled': {
    backgroundColor: 'rgba(71, 85, 105, 0.52)',
    color: 'rgba(226, 232, 240, 0.6)',
  },
  '&.MuiButton-outlined.Mui-disabled': {
    backgroundColor: 'transparent',
  },
}

const getFieldHelperText = (
  blurredFields: Record<string, boolean>,
  field: string,
  errorMessage: string
) =>
  blurredFields[field] && errorMessage !== '' ? errorMessage : ' '

const getFieldError = (
  blurredFields: Record<string, boolean>,
  field: string,
  errorMessage: string
) => blurredFields[field] && errorMessage !== ''

export const CreateCustomerRentalWorkflowModal: React.FC<Props> = ({
  mode = 'customerRental',
  open,
  onClose,
  onCompleted,
}) => {
  const masterUnits = useSelector(getUnitsMaster)
  const individualUnits = useSelector(getUnitsIndividual)
  const [activeStep, setActiveStep] = React.useState(0)
  const [customer, setCustomer] = React.useState<CustomerState>(EMPTY_CUSTOMER)
  const [payment, setPayment] = React.useState<PaymentState>(EMPTY_PAYMENT)
  const [workflowInventoryGroups, setWorkflowInventoryGroups] = React.useState<
    InventoryGroup[]
  >([])
  const [requestedByMasterId, setRequestedByMasterId] = React.useState<
    Record<string, number>
  >({})
  const [selectedUnitIds, setSelectedUnitIds] = React.useState<string[]>([])
  const [submitting, setSubmitting] = React.useState(false)
  const [claimCode, setClaimCode] = React.useState('')
  const [blurredFields, setBlurredFields] = React.useState<
    Record<string, boolean>
  >({})
  const submitLockRef = React.useRef(false)
  const isProfileOnly = mode === 'profileOnly'
  const steps = isProfileOnly ? PROFILE_ONLY_STEPS : RENTAL_STEPS
  const summaryStepIndex = steps.length - 1
  const isUnitMixStep = !isProfileOnly && activeStep === 2
  const isUnitLocationsStep = !isProfileOnly && activeStep === 3

  const customerEmailError = getRequiredEmailError(customer.email)
  const customerPhoneError = getRequiredPhoneError(
    customer.phoneNumber,
    'Phone number'
  )
  const customerFirstNameError = getRequiredTextError(
    customer.firstName,
    'First name'
  )
  const customerLastNameError = getRequiredTextError(
    customer.lastName,
    'Last name'
  )
  const billingAddressError = getRequiredTextError(
    payment.address,
    'Billing address'
  )
  const billingCityError = getRequiredTextError(payment.city, 'City')
  const billingZipError = getRequiredZipError(payment.zip, 'ZIP')
  const billingCardNumberError = getRequiredCardNumberError(
    payment.cardNumber
  )
  const billingExpirationError = getRequiredExpirationDateError(
    payment.expirationDate
  )

  const shipping = React.useMemo<ShippingState>(
    () => ({
      firstName: customer.firstName.trim(),
      lastName: customer.lastName.trim(),
      phoneNumber: customer.phoneNumber.trim(),
      address: payment.address.trim(),
      city: payment.city.trim(),
      state: payment.state.trim(),
      zip: payment.zip.trim(),
    }),
    [customer, payment]
  )

  const liveInventoryGroups = React.useMemo<InventoryGroup[]>(() => {
    return masterUnits
      .filter((masterUnit) => masterUnit.id)
      .map((masterUnit) => {
        const availableUnits = individualUnits
          .filter(
            (individualUnit) =>
              individualUnit.masterId === masterUnit.id &&
              !individualUnit.customerProfileId &&
              Boolean(individualUnit.unitNumber)
          )
          .map((individualUnit) => ({
            id: String(individualUnit.id),
            masterId: String(masterUnit.id),
            unitNumber: String(individualUnit.unitNumber),
            size: masterUnit.size,
            isClimate: Boolean(masterUnit.isClimate),
            isCovered: Boolean(masterUnit.isCovered),
          }))
          .sort((left, right) => left.unitNumber.localeCompare(right.unitNumber))

        return {
          masterId: String(masterUnit.id),
          label: getInventoryLabel(masterUnit),
          price: Number(masterUnit.price ?? 0),
          availableUnits,
        }
      })
      .filter((group) => group.availableUnits.length > 0)
  }, [individualUnits, masterUnits])

  React.useEffect(() => {
    if (!open) {
      setWorkflowInventoryGroups([])
      submitLockRef.current = false
      return
    }

    setWorkflowInventoryGroups((current) => {
      if (current.length > 0 || liveInventoryGroups.length === 0) {
        return current
      }

      return liveInventoryGroups
    })
  }, [liveInventoryGroups, open])

  const priceByMasterId = React.useMemo(
    () =>
      workflowInventoryGroups.reduce<Record<string, number>>(
        (accumulator, group) => {
          accumulator[group.masterId] = group.price
          return accumulator
        },
        {}
      ),
    [workflowInventoryGroups]
  )

  const selectedUnits = React.useMemo(
    () =>
      workflowInventoryGroups.flatMap((group) =>
        group.availableUnits.filter((unit) => selectedUnitIds.includes(unit.id))
      ),
    [selectedUnitIds, workflowInventoryGroups]
  )

  const customerStepValid =
    customerEmailError === '' &&
    customerFirstNameError === '' &&
    customerLastNameError === '' &&
    customerPhoneError === ''

  const billingStepValid =
    billingAddressError === '' &&
    billingCityError === '' &&
    payment.state.trim() !== '' &&
    billingZipError === '' &&
    billingCardNumberError === '' &&
    billingExpirationError === ''

  React.useEffect(() => {
    if (!open) {
      setActiveStep(0)
      setCustomer(EMPTY_CUSTOMER)
      setPayment(EMPTY_PAYMENT)
      setWorkflowInventoryGroups([])
      setRequestedByMasterId({})
      setSelectedUnitIds([])
      setSubmitting(false)
      setClaimCode('')
      setBlurredFields({})
      submitLockRef.current = false
    }
  }, [open])

  const markFieldBlurred = React.useCallback((field: string) => {
    setBlurredFields((current) =>
      current[field] ? current : { ...current, [field]: true }
    )
  }, [])

  const isStepValid = React.useMemo(() => {
    if (activeStep === 0) {
      return customerStepValid
    }

    if (activeStep === 1) {
      return billingStepValid
    }

    if (isUnitMixStep) {
      return Object.values(requestedByMasterId).some((value) => value > 0)
    }

    if (isUnitLocationsStep) {
      return workflowInventoryGroups.every((group) => {
        const requested = requestedByMasterId[group.masterId] ?? 0
        const selected = selectedUnits.filter(
          (unit) => unit.masterId === group.masterId
        ).length
        return requested === selected
      })
    }

    return true
  }, [
    activeStep,
    billingStepValid,
    customerStepValid,
    isUnitLocationsStep,
    isUnitMixStep,
    requestedByMasterId,
    selectedUnits,
    workflowInventoryGroups,
  ])

  const nextDisabledReason = React.useMemo(() => {
    if (submitting) {
      return 'Wait for the current request to finish.'
    }

    if (isStepValid) {
      return ''
    }

    if (activeStep === 0) {
      return (
        customerEmailError ||
        customerPhoneError ||
        customerFirstNameError ||
        customerLastNameError ||
        'Complete the customer form.'
      )
    }

    if (activeStep === 1) {
      return (
        billingAddressError ||
        billingCityError ||
        billingZipError ||
        billingCardNumberError ||
        billingExpirationError ||
        'Complete the billing form.'
      )
    }

    if (isUnitMixStep) {
      return 'Choose at least one unit type and quantity.'
    }

    if (isUnitLocationsStep) {
      return 'Match every requested unit with a specific unit location.'
    }

    return 'Complete the required fields before continuing.'
  }, [
    activeStep,
    billingAddressError,
    billingCardNumberError,
    billingCityError,
    billingExpirationError,
    billingZipError,
    customerEmailError,
    customerFirstNameError,
    customerLastNameError,
    customerPhoneError,
    isStepValid,
    isUnitLocationsStep,
    isUnitMixStep,
    submitting,
  ])

  const previousDisabledReason = React.useMemo(() => {
    if (submitting) {
      return 'Wait for the current request to finish.'
    }

    if (activeStep === 0) {
      return 'You are already on the first step.'
    }

    return ''
  }, [activeStep, submitting])

  const handleToggleUnitSelection = React.useCallback(
    (unitId: string, masterId: string) => {
      setSelectedUnitIds((current) => {
        if (current.includes(unitId)) {
          return current.filter((id) => id !== unitId)
        }

        const requested = requestedByMasterId[masterId] ?? 0
        const selectedCount = selectedUnits.filter(
          (unit) => unit.masterId === masterId
        ).length

        if (selectedCount >= requested) {
          return current
        }

        return [...current, unitId]
      })
    },
    [requestedByMasterId, selectedUnits]
  )

  const handleSubmit = React.useCallback(async () => {
    if (submitLockRef.current) {
      return
    }

    if (!isProfileOnly && selectedUnits.length === 0) {
      onError('Select at least one unit before submitting.')
      return
    }

    submitLockRef.current = true
    setSubmitting(true)
    try {
      const config = await getApiConfig({
        body: {
          workflowMode: mode,
          customer,
          payment,
          shipping,
          assignedUnits: selectedUnits.map((unit) => ({
            ...unit,
            price: priceByMasterId[unit.masterId] ?? 0,
          })),
        },
      })

      if (config == null) {
        onError('Could not authorize the admin workflow')
        return
      }

      const response = await fetch(
        getLocalServerURL(API_PATH_ADMIN_CREATE_CUSTOMER_RENTAL),
        config
      )
      const data = (await response.json().catch(() => ({}))) as {
        messages?: { resultCode?: string; message?: Array<{ text?: string }> }
        customerProfileId?: number
        claimCode?: string
      }

      if (
        !response.ok ||
        data.messages?.resultCode !== 'Ok' ||
        data.customerProfileId == null
      ) {
        throw new Error(
          data.messages?.message?.[0]?.text ||
            'Failed to create customer rental workflow'
        )
      }

      setClaimCode(data.claimCode ?? '')
      onSuccess(
        isProfileOnly
          ? 'Customer profile workflow completed'
          : 'Customer rental workflow completed'
      )
      onCompleted({
        customerProfileId: data.customerProfileId,
        customerName: `${customer.firstName} ${customer.lastName}`.trim(),
        claimCode: data.claimCode ?? '',
      })
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : 'Failed to create customer rental workflow'
      )
    } finally {
      submitLockRef.current = false
      setSubmitting(false)
    }
  }, [
    customer,
    isProfileOnly,
    mode,
    onCompleted,
    payment,
    priceByMasterId,
    selectedUnits,
    shipping,
  ])

  const renderSummaryContent = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        {isProfileOnly
          ? 'This will create the customer profile, payment profile, and an unclaimed portal-access record without assigning any units yet.'
          : 'The customer will receive an unclaimed portal-access record with a claim code after submission.'}
      </Alert>
      <Typography variant="body2">
        Customer: {customer.firstName} {customer.lastName} · {customer.email}
      </Typography>
      <Typography variant="body2">
        Billing: {payment.address}, {payment.city}, {payment.state} {payment.zip}
      </Typography>
      {!isProfileOnly ? (
        <>
          <Typography variant="body2">
            Units:{' '}
            {selectedUnits.map((unit) => `${unit.size}/${unit.unitNumber}`).join(', ')}
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            Monthly total: $
            {getSummaryTotal(selectedUnits, priceByMasterId).toFixed(2)}
          </Typography>
        </>
      ) : null}
      {claimCode ? (
        <Alert severity="success">Claim code: {claimCode}</Alert>
      ) : null}
    </Stack>
  )

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              fullWidth
              value={customer.email}
              error={getFieldError(blurredFields, 'customer.email', customerEmailError)}
              helperText={getFieldHelperText(
                blurredFields,
                'customer.email',
                customerEmailError
              )}
              onChange={(event) =>
                setCustomer((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('customer.email')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Phone number"
              fullWidth
              value={customer.phoneNumber}
              error={getFieldError(
                blurredFields,
                'customer.phoneNumber',
                customerPhoneError
              )}
              helperText={getFieldHelperText(
                blurredFields,
                'customer.phoneNumber',
                customerPhoneError
              )}
              onChange={(event) =>
                setCustomer((current) => ({
                  ...current,
                  phoneNumber: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('customer.phoneNumber')}
              InputProps={{
                inputComponent: TextMaskPhoneNumber as any,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="First name"
              fullWidth
              value={customer.firstName}
              error={getFieldError(
                blurredFields,
                'customer.firstName',
                customerFirstNameError
              )}
              helperText={getFieldHelperText(
                blurredFields,
                'customer.firstName',
                customerFirstNameError
              )}
              onChange={(event) =>
                setCustomer((current) => ({
                  ...current,
                  firstName: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('customer.firstName')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Last name"
              fullWidth
              value={customer.lastName}
              error={getFieldError(
                blurredFields,
                'customer.lastName',
                customerLastNameError
              )}
              helperText={getFieldHelperText(
                blurredFields,
                'customer.lastName',
                customerLastNameError
              )}
              onChange={(event) =>
                setCustomer((current) => ({
                  ...current,
                  lastName: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('customer.lastName')}
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">
              This workflow creates an unclaimed portal-access record. The
              customer can create an online login later with the generated claim
              code.
            </Alert>
          </Grid>
        </Grid>
      )
    }

    if (activeStep === 1) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info">
              Shipping will automatically use the same contact and address as
              billing for this workflow.
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Billing address"
              fullWidth
              value={payment.address}
              error={getFieldError(
                blurredFields,
                'payment.address',
                billingAddressError
              )}
              helperText={getFieldHelperText(
                blurredFields,
                'payment.address',
                billingAddressError
              )}
              onChange={(event) =>
                setPayment((current) => ({
                  ...current,
                  address: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('payment.address')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="City"
              fullWidth
              value={payment.city}
              error={getFieldError(
                blurredFields,
                'payment.city',
                billingCityError
              )}
              helperText={getFieldHelperText(
                blurredFields,
                'payment.city',
                billingCityError
              )}
              onChange={(event) =>
                setPayment((current) => ({
                  ...current,
                  city: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('payment.city')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="State"
              fullWidth
              value={payment.state}
              onChange={(event) =>
                setPayment((current) => ({
                  ...current,
                  state: event.target.value,
                }))
              }
            >
              {STATES.map((state) => (
                <MenuItem key={state.value} value={state.value}>
                  {state.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="ZIP"
              fullWidth
              value={payment.zip}
              error={getFieldError(blurredFields, 'payment.zip', billingZipError)}
              helperText={getFieldHelperText(
                blurredFields,
                'payment.zip',
                billingZipError
              )}
              onChange={(event) =>
                setPayment((current) => ({
                  ...current,
                  zip: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('payment.zip')}
              InputProps={{
                inputComponent: TextMaskZipcode as any,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Card number"
              fullWidth
              value={payment.cardNumber}
              error={getFieldError(
                blurredFields,
                'payment.cardNumber',
                billingCardNumberError
              )}
              helperText={getFieldHelperText(
                blurredFields,
                'payment.cardNumber',
                billingCardNumberError
              )}
              onChange={(event) =>
                setPayment((current) => ({
                  ...current,
                  cardNumber: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('payment.cardNumber')}
              InputProps={{
                inputComponent: TextMaskCreditCard as any,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Expiration date"
              fullWidth
              value={payment.expirationDate}
              error={getFieldError(
                blurredFields,
                'payment.expirationDate',
                billingExpirationError
              )}
              helperText={getFieldHelperText(
                blurredFields,
                'payment.expirationDate',
                billingExpirationError
              )}
              onChange={(event) =>
                setPayment((current) => ({
                  ...current,
                  expirationDate: event.target.value,
                }))
              }
              onBlur={() => markFieldBlurred('payment.expirationDate')}
              InputProps={{
                inputComponent: TextMaskCreditCardDate as any,
              }}
            />
          </Grid>
        </Grid>
      )
    }

    if (isUnitMixStep) {
      return (
        <Grid container spacing={2}>
          {workflowInventoryGroups.map((group) => (
            <Grid item xs={12} md={6} key={group.masterId}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {group.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${group.price.toFixed(2)} / month ·{' '}
                    {group.availableUnits.length} available
                  </Typography>
                  <TextField
                    label="Quantity"
                    type="number"
                    inputProps={{
                      min: 0,
                      max: group.availableUnits.length,
                    }}
                    value={requestedByMasterId[group.masterId] ?? 0}
                    onChange={(event) =>
                      setRequestedByMasterId((current) => ({
                        ...current,
                        [group.masterId]: Math.max(
                          0,
                          Math.min(
                            group.availableUnits.length,
                            Number(event.target.value || 0)
                          )
                        ),
                      }))
                    }
                  />
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )
    }

    if (isUnitLocationsStep) {
      return (
        <Stack spacing={2}>
          {workflowInventoryGroups
            .filter((group) => (requestedByMasterId[group.masterId] ?? 0) > 0)
            .map((group) => {
              const requested = requestedByMasterId[group.masterId] ?? 0
              const selectedCount = selectedUnits.filter(
                (unit) => unit.masterId === group.masterId
              ).length

              return (
                <Paper variant="outlined" sx={{ p: 2 }} key={group.masterId}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {group.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select {requested} unit location
                      {requested === 1 ? '' : 's'}. Currently selected:{' '}
                      {selectedCount}
                    </Typography>
                    {group.availableUnits.map((unit) => {
                      const checked = selectedUnitIds.includes(unit.id)
                      const disableSelection = !checked && selectedCount >= requested

                      return (
                        <FormControlLabel
                          key={unit.id}
                          control={
                            <Checkbox
                              checked={checked}
                              disabled={disableSelection}
                              onChange={() =>
                                handleToggleUnitSelection(unit.id, group.masterId)
                              }
                            />
                          }
                          label={`${unit.unitNumber} · ${unit.size}`}
                        />
                      )
                    })}
                  </Stack>
                </Paper>
              )
            })}
        </Stack>
      )
    }

    return renderSummaryContent()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(6, 10, 18, 0.62)',
          },
        },
      }}
    >
      <DialogTitle>
        {isProfileOnly ? 'Create Customer Profile' : 'Create Customer + Rentals'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent()}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Tooltip
          title={submitting ? 'Wait for submission to finish.' : ''}
          disableHoverListener={!submitting}
        >
          <span>
            <Button onClick={onClose} disabled={submitting} sx={disabledButtonSx}>
              Cancel
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={previousDisabledReason}
          disableHoverListener={!previousDisabledReason}
        >
          <span>
            <Button
              onClick={() => setActiveStep((current) => Math.max(current - 1, 0))}
              disabled={activeStep === 0 || submitting}
              sx={disabledButtonSx}
            >
              Previous
            </Button>
          </span>
        </Tooltip>
        {activeStep === summaryStepIndex ? (
          <Tooltip
            title={nextDisabledReason}
            disableHoverListener={!nextDisabledReason}
          >
            <span>
              <Button
                variant="contained"
                onClick={() => {
                  void handleSubmit()
                }}
                disabled={!isStepValid || submitting}
                sx={disabledButtonSx}
              >
                {submitting
                  ? 'Submitting…'
                  : isProfileOnly
                    ? 'Create profile'
                    : 'Submit'}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            title={nextDisabledReason}
            disableHoverListener={!nextDisabledReason}
          >
            <span>
              <Button
                variant="contained"
                onClick={() =>
                  setActiveStep((current) =>
                    Math.min(current + 1, summaryStepIndex)
                  )
                }
                disabled={!isStepValid || submitting}
                sx={disabledButtonSx}
              >
                Next
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  )
}
